import { useEffect, useState, useCallback } from "react";
import { API, api } from "./api";
import { OfflineQueue } from "./offlineQueue";
import { toast } from "sonner";

/** React hook: current online/offline state + queued count + manual sync trigger. */
export function useOfflineStatus() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    const items = await OfflineQueue.listQueue();
    setPending(items.length);
  }, []);

  useEffect(() => {
    refreshPending();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsub = OfflineQueue.subscribe(refreshPending);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsub();
    };
  }, [refreshPending]);

  // Auto-sync when we come back online
  useEffect(() => {
    if (!online || pending === 0) return;
    syncNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const syncNow = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { synced, failed } = await OfflineQueue.flushQueue(API);
      if (synced > 0) toast.success(`Synced ${synced} draft${synced === 1 ? "" : "s"}`);
      if (failed > 0) toast.error(`${failed} draft${failed === 1 ? "" : "s"} failed`);
      await refreshPending();
    } finally {
      setSyncing(false);
    }
  }, [syncing, refreshPending]);

  return { online, pending, syncing, syncNow };
}

/** Ensure the api instance (unused ref) is bundled */
export const __api_ref = api;
